import { canChooseOutput } from '../audio/output'
import { readStoredJson, writeStoredString } from '../storedFlag'

/** The mic and speakers chosen for huddles; null is the system default. */
export interface IHuddleDevices {
  inputId: string | null
  outputId: string | null
}

const STORAGE_KEY = 'huddle.devices'
const NO_CHOICE: IHuddleDevices = { inputId: null, outputId: null }

const readStored = (): IHuddleDevices => {
  const stored = readStoredJson(STORAGE_KEY)
  return stored && typeof stored === 'object'
    ? { ...NO_CHOICE, ...(stored as Partial<IHuddleDevices>) }
    : NO_CHOICE
}

/** Kept per browser: device ids mean nothing on another machine. */
class HuddleDeviceStore {
  private devices: IHuddleDevices | null = null

  private readonly listeners = new Set<(devices: IHuddleDevices) => void>()

  get(): IHuddleDevices {
    this.devices ??= readStored()
    return this.devices
  }

  set(patch: Partial<IHuddleDevices>): void {
    this.devices = { ...this.get(), ...patch }
    writeStoredString(STORAGE_KEY, JSON.stringify(this.devices))
    const { devices } = this
    this.listeners.forEach((listener) => listener(devices))
  }

  subscribe(listener: (devices: IHuddleDevices) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
}

export const huddleDevices = new HuddleDeviceStore()

export interface IAudioDeviceLists {
  inputs: MediaDeviceInfo[]
  outputs: MediaDeviceInfo[]
}

/** Named once the mic is allowed, which it is by the time anyone is in a huddle. */
export const listAudioDevices = async (): Promise<IAudioDeviceLists> => {
  const devices = await navigator.mediaDevices.enumerateDevices()
  const isReal = ({ deviceId }: MediaDeviceInfo) => !!deviceId && deviceId !== 'default'
  return {
    inputs: devices.filter((device) => device.kind === 'audioinput' && isReal(device)),
    outputs: canChooseOutput()
      ? devices.filter((device) => device.kind === 'audiooutput' && isReal(device))
      : [],
  }
}
